import { useState, useCallback } from 'react';
import size from 'lodash/size';

import {
  FetchProductsSetsCacheKey,
  FetchProductsSetByUserIdScopeType
} from '../../../../../../productsSets/productsSetsTypes';

import {
  FETCH_PRODUCTS_SET_BY_USER_ID_QUERY,
  FetchProductsSetByUserIdQueryResponse
} from '../../../../../../productsSets/queries/fetchProductsSetByUserId.query';

import { useProductsSetByUserId } from '../../../../../../productsSets/hooks/useProductsSetByUserId';
import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';

import { ProductsListOnProductsSelect } from '../../../../lists/ProductsList';

interface ProductsIndexContentSelectedProductsOptions {
  productsSetScope?: FetchProductsSetByUserIdScopeType;
  productsSetCacheKey: FetchProductsSetsCacheKey;
  onProductsSelect?: ProductsListOnProductsSelect;
}

function useProductsIndexContentSelectedProducts({
  productsSetScope,
  productsSetCacheKey,
  onProductsSelect
}: ProductsIndexContentSelectedProductsOptions) {
  const currentUser = useCurrentUser();

  const [isSelectedProductsSidebarOpen, setIsSelectedProductsSidebarOpen] =
    useState(false);

  const handleSelectedProductsSidebarOpen = useCallback<() => void>(() => {
    setIsSelectedProductsSidebarOpen(true);
  }, [setIsSelectedProductsSidebarOpen]);

  const handleSelectedProductsSidebarClose = useCallback<() => void>(() => {
    setIsSelectedProductsSidebarOpen(false);
  }, [setIsSelectedProductsSidebarOpen]);

  const {
    productsSet,
    productsSetError,
    productsSetFetched,
    productsSetIsPlaceholderData,
    updateProductsSetCache
  } = useProductsSetByUserId<FetchProductsSetByUserIdQueryResponse>({
    cacheKey: productsSetCacheKey,
    query: FETCH_PRODUCTS_SET_BY_USER_ID_QUERY,
    userId: currentUser.get('id'),
    scope: productsSetScope,
    options: {
      onSuccess: (data) => {
        const selectedProductsAmount = size(
          data?.productsSetByUserId?.selectedProducts
        );

        if (selectedProductsAmount === 0 && isSelectedProductsSidebarOpen) {
          handleSelectedProductsSidebarClose();
        }
        if (selectedProductsAmount > 0 && !isSelectedProductsSidebarOpen) {
          handleSelectedProductsSidebarOpen();
        }

        data?.productsSetByUserId?.selectedProducts
          ? onProductsSelect?.(data?.productsSetByUserId?.selectedProducts)
          : undefined;
      }
    }
  });

  return {
    productsSet,
    productsSetError,
    productsSetFetched,
    isOptimisticUpdateData: productsSet?.optimisticUpdate,
    productsSetIsPlaceholderData,
    updateProductsSetCache,
    isSelectedProductsSidebarOpen,
    handleSelectedProductsSidebarOpen,
    handleSelectedProductsSidebarClose
  };
}

export default useProductsIndexContentSelectedProducts;
